/**
 * Inline i18n dictionary for the widget.
 *
 * We ship a small static dictionary (en/it/es) inside the bundle rather than
 * fetching JSON at runtime — keeps the cold-start latency on the customer
 * site to zero and avoids an extra CORS hop. The active locale is decided
 * by, in order:
 *
 *   1. Saved widget locale (the visitor can change it using the picker)
 *   2. The `lang` attribute on `<deepagent-agent>`
 *   3. `document.documentElement.lang`
 *   4. `navigator.language`
 *   5. English fallback
 *
 * Custom owner wording is preserved; stock labels follow the active locale.
 */

export type SupportedLocale = "en" | "it" | "es";

export interface I18nStrings {
  // Panel header
  poweredBy: string;
  // States
  connecting: string;
  connected: string;
  callEnded: string;
  /** Header status before any session: the agent is there to answer. */
  online: string;
  unavailable: string;
  // Buttons
  startCall: string;
  /** Consent accepted on a widget that then offers both a call and a chat. */
  acceptTerms: string;
  endCall: string;
  muteMicrophone: string;
  unmuteMicrophone: string;
  close: string;
  // Microphone pre-flight
  micPrompt: string;
  micPromptHint: string;
  // Errors
  micPermissionDenied: string;
  micNotFound: string;
  micUnavailable: string;
  webrtcUnsupported: string;
  networkError: string;
  agentNotDeployed: string;
  originNotAllowed: string;
  previewOnly: string;
  variableConfigurationError: string;
  rateLimited: string;
  // Language picker
  changeLanguage: string;
  // Transcript
  transcriptYou: string;
  transcriptAgent: string;
  transcriptHint: string;
  // Chat
  startChat: string;
  composerPlaceholder: string;
  send: string;
  agentTyping: string;
  /** Header status while a chat is running, where a call reads "Live". */
  chatActive: string;
  chatEnded: string;
  chatHint: string;
  /** The small text link that closes a running chat. */
  endChat: string;
  // Choosing how to talk
  /** On the call stage before a voice-only call. */
  callHint: string;
  /** Back from the chat to the choice between a call and a chat. */
  back: string;
  /** The visitor's line in the builder preview of a running call or chat. */
  previewVisitorLine: string;
  /** Under the call stage before a call: when the microphone is on. */
  micNotice: string;
  agentSpeaking: string;
  agentListening: string;
  /** Under the launcher label, naming what pressing it opens. */
  launcherVoice: string;
  launcherText: string;
}

const en: I18nStrings = {
  poweredBy: "Powered by DeepAgent",
  connecting: "Connecting…",
  connected: "In call",
  callEnded: "Call ended",
  online: "Online",
  unavailable: "Currently unavailable",
  startCall: "Call",
  acceptTerms: "Continue",
  endCall: "End call",
  muteMicrophone: "Mute microphone",
  unmuteMicrophone: "Unmute microphone",
  close: "Close",
  changeLanguage: "Change language",
  micPrompt: "Allow your microphone",
  micPromptHint:
    "Your browser is asking for permission. We only listen while the call is running.",
  micPermissionDenied:
    "Microphone access is blocked. Allow it from the padlock icon in the address bar, then try again.",
  micNotFound:
    "No microphone found. Connect one, or check it is selected in your system settings.",
  micUnavailable:
    "Your microphone is busy. Another app may be using it: close it and try again.",
  webrtcUnsupported:
    "Your browser does not support voice calls. Try a modern browser like Chrome, Safari, or Firefox.",
  networkError:
    "We could not start the call. Please check your connection and try again.",
  agentNotDeployed:
    "This agent is not published yet. It has to be activated before it can take calls.",
  originNotAllowed:
    "This site is not authorised for this agent. Add its address to the allowed domains.",
  previewOnly: "This is a preview. Publish the agent to take real calls.",
  variableConfigurationError:
    "The assistant needs information that this site has not provided correctly. Please contact the site owner.",
  rateLimited: "Too many attempts. Please wait before trying again.",
  transcriptYou: "You",
  transcriptAgent: "Agent",
  transcriptHint: "Your conversation will appear here.",
  startChat: "Write to us",
  composerPlaceholder: "Write a message…",
  send: "Send",
  agentTyping: "Writing…",
  chatActive: "Chat",
  chatEnded: "Chat ended",
  chatHint: "Write a message to start.",
  endChat: "End chat",
  callHint: "Talk it through now, by voice",
  back: "Back",
  previewVisitorLine: "I'd like to book an appointment.",
  micNotice: "The microphone is only on during the call.",
  agentSpeaking: "Speaking…",
  agentListening: "Listening…",
  launcherVoice: "Voice call",
  launcherText: "Chat",
};

const it: I18nStrings = {
  poweredBy: "Powered by DeepAgent",
  connecting: "Connessione in corso…",
  connected: "In chiamata",
  callEnded: "Chiamata terminata",
  online: "Disponibile",
  unavailable: "Servizio non disponibile",
  startCall: "Chiama",
  acceptTerms: "Continua",
  endCall: "Termina chiamata",
  muteMicrophone: "Disattiva microfono",
  unmuteMicrophone: "Attiva microfono",
  close: "Chiudi",
  changeLanguage: "Cambia lingua",
  micPrompt: "Consenti il microfono",
  micPromptHint:
    "Il browser ti sta chiedendo il permesso. Ascoltiamo solo mentre la chiamata è in corso.",
  micPermissionDenied:
    "L'accesso al microfono è bloccato. Abilitalo dall'icona del lucchetto nella barra degli indirizzi, poi riprova.",
  micNotFound:
    "Nessun microfono rilevato. Collegane uno, oppure controlla che sia selezionato nelle impostazioni di sistema.",
  micUnavailable:
    "Il microfono è occupato. Potrebbe usarlo un'altra app: chiudila e riprova.",
  webrtcUnsupported:
    "Il tuo browser non supporta le chiamate vocali. Prova un browser moderno come Chrome, Safari o Firefox.",
  networkError:
    "Non siamo riusciti ad avviare la chiamata. Controlla la connessione e riprova.",
  agentNotDeployed:
    "Questo agente non è ancora pubblicato. Va attivato prima di poter ricevere chiamate.",
  originNotAllowed:
    "Questo sito non è autorizzato per questo agente. Aggiungi il suo indirizzo ai domini consentiti.",
  previewOnly:
    "Questa è un'anteprima. Pubblica l'agente per ricevere chiamate reali.",
  variableConfigurationError:
    "L'assistente richiede informazioni che questo sito non ha fornito correttamente. Contatta il gestore del sito.",
  rateLimited: "Troppi tentativi. Attendi prima di riprovare.",
  transcriptYou: "Tu",
  transcriptAgent: "Agente",
  transcriptHint: "Qui apparirà la trascrizione della conversazione.",
  startChat: "Scrivici",
  composerPlaceholder: "Scrivi un messaggio…",
  send: "Invia",
  agentTyping: "Sta scrivendo…",
  chatActive: "Chat",
  chatEnded: "Chat terminata",
  chatHint: "Scrivi un messaggio per iniziare.",
  endChat: "Termina chat",
  callHint: "Parla a voce, subito",
  back: "Indietro",
  previewVisitorLine: "Vorrei prenotare un appuntamento.",
  micNotice: "Il microfono si attiva solo durante la chiamata.",
  agentSpeaking: "Sta parlando…",
  agentListening: "In ascolto…",
  launcherVoice: "Chiamata vocale",
  launcherText: "Chat",
};

const es: I18nStrings = {
  poweredBy: "Powered by DeepAgent",
  connecting: "Conectando…",
  connected: "En llamada",
  callEnded: "Llamada finalizada",
  online: "Disponible",
  unavailable: "Servicio no disponible",
  startCall: "Llamar",
  acceptTerms: "Continuar",
  endCall: "Finalizar llamada",
  muteMicrophone: "Silenciar micrófono",
  unmuteMicrophone: "Reactivar micrófono",
  close: "Cerrar",
  changeLanguage: "Cambiar idioma",
  micPrompt: "Permite el micrófono",
  micPromptHint:
    "El navegador te está pidiendo permiso. Solo escuchamos mientras dura la llamada.",
  micPermissionDenied:
    "El acceso al micrófono está bloqueado. Permítelo desde el icono del candado en la barra de direcciones y vuelve a intentarlo.",
  micNotFound:
    "No se ha detectado ningún micrófono. Conecta uno o comprueba que esté seleccionado en la configuración del sistema.",
  micUnavailable:
    "El micrófono está ocupado. Puede que lo esté usando otra app: ciérrala y vuelve a intentarlo.",
  webrtcUnsupported:
    "Tu navegador no admite llamadas de voz. Prueba con un navegador moderno como Chrome, Safari o Firefox.",
  networkError:
    "No pudimos iniciar la llamada. Comprueba tu conexión y vuelve a intentarlo.",
  agentNotDeployed:
    "Este agente aún no está publicado. Debe activarse antes de poder recibir llamadas.",
  originNotAllowed:
    "Este sitio no está autorizado para este agente. Añade su dirección a los dominios permitidos.",
  previewOnly:
    "Esto es una vista previa. Publica el agente para recibir llamadas reales.",
  variableConfigurationError:
    "El asistente necesita información que este sitio no ha proporcionado correctamente. Contacta con el responsable del sitio.",
  rateLimited: "Demasiados intentos. Espera antes de volver a intentarlo.",
  transcriptYou: "Tú",
  transcriptAgent: "Agente",
  transcriptHint: "Aquí aparecerá la conversación.",
  startChat: "Escríbenos",
  composerPlaceholder: "Escribe un mensaje…",
  send: "Enviar",
  agentTyping: "Escribiendo…",
  chatActive: "Chat",
  chatEnded: "Chat finalizada",
  chatHint: "Escribe un mensaje para empezar.",
  endChat: "Finalizar chat",
  callHint: "Habla por voz, ahora",
  back: "Atrás",
  previewVisitorLine: "Quisiera reservar una cita.",
  micNotice: "El micrófono solo se activa durante la llamada.",
  agentSpeaking: "Hablando…",
  agentListening: "Escuchando…",
  launcherVoice: "Llamada de voz",
  launcherText: "Chat",
};

const DICTIONARIES: Record<SupportedLocale, I18nStrings> = { en, it, es };

/** The visitor's language when the bundle has wording for it, else `null`. */
export function detectLocale(
  explicit: string | null | undefined,
): SupportedLocale | null {
  const candidates = [
    explicit,
    typeof document !== "undefined" ? document.documentElement.lang : null,
    typeof navigator !== "undefined" ? navigator.language : null,
  ];
  for (const raw of candidates) {
    if (!raw) continue;
    const short = raw.slice(0, 2).toLowerCase();
    if (short === "it" || short === "es" || short === "en") {
      return short;
    }
  }
  return null;
}

/**
 * The embedding page's `lang` wins; then the visitor's language if the agent
 * speaks it, else the agent's, so the labels match the greeting.
 */
export function visitorLocale(
  explicit: string | null | undefined,
  agentLanguages: string[] | null | undefined,
): SupportedLocale {
  const short = explicit?.slice(0, 2).toLowerCase() ?? "";
  if (isSupportedLocale(short)) return short;
  const visitor = detectLocale(null);
  const spoken = selectableLocales(agentLanguages);
  if (visitor && (spoken.length === 0 || spoken.includes(visitor))) {
    return visitor;
  }
  return agentLocale(agentLanguages) ?? visitor ?? "en";
}

export function resolveLocale(
  explicit: string | null | undefined,
): SupportedLocale {
  return detectLocale(explicit) ?? "en";
}

/** The first of the agent's own languages the bundle can translate, in the agent's order. */
export function agentLocale(
  agentLanguages: string[] | null | undefined,
): SupportedLocale | null {
  for (const raw of agentLanguages ?? []) {
    const short = raw.slice(0, 2).toLowerCase();
    if (isSupportedLocale(short)) return short;
  }
  return null;
}

export function getStrings(locale: SupportedLocale): I18nStrings {
  return DICTIONARIES[locale] ?? DICTIONARIES.en;
}

/** Endonyms for the picker — a locale is always named in its own language. */
export const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: "English",
  it: "Italiano",
  es: "Español",
};

export const SUPPORTED_LOCALES: SupportedLocale[] = ["en", "it", "es"];

export function isSupportedLocale(value: string): value is SupportedLocale {
  return value === "en" || value === "it" || value === "es";
}

/**
 * The locales the picker may offer: the agent's own languages narrowed to what
 * the bundle can actually translate, de-duplicated and order-preserving. An
 * agent configured only in one language gets no picker at all.
 */
export function selectableLocales(
  agentLanguages: string[] | null | undefined,
): SupportedLocale[] {
  if (!agentLanguages?.length) return [];
  const seen = new Set<SupportedLocale>();
  for (const raw of agentLanguages) {
    const short = raw.slice(0, 2).toLowerCase();
    if (isSupportedLocale(short)) seen.add(short);
  }
  return SUPPORTED_LOCALES.filter((l) => seen.has(l));
}
